import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, SectionHeader } from '@/components/ui/card';
import { Table, TBody, TD, TH, THead, TR, TableWrap } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Spinner } from '@/components/ui/feedback';
import { tabfile } from '@/lib/api';
import { formSchemaQuery } from '@/lib/queries';

/** What a column is read as when the customer does not want it read at all. */
const IGNORE = '__ignore__';

/** What detection decided, before anybody disagreed with it. */
const DETECT = '__detect__';

export type MappingRow = {
  header: string;
  detected: string | null;
  /** undefined: nothing said. null: told to ignore it. string: told what it means. */
  stated: string | null | undefined;
};

/** The rows of the mapping table: everything the file has, plus anything ruled out. */
export function mappingRows(
  mapping: Record<string, string>,
  unmapped: string[],
  overrides: Record<string, string | null> | undefined,
): MappingRow[] {
  const headers = [...Object.keys(mapping), ...unmapped];
  // A column the customer told us to ignore is in neither list, and still has to be shown:
  // otherwise the decision becomes invisible and cannot be taken back.
  for (const header of Object.keys(overrides ?? {})) {
    if (!headers.includes(header)) headers.push(header);
  }
  return headers.map((header) => ({
    header,
    detected: mapping[header] ?? null,
    stated: overrides && header in overrides ? overrides[header] : undefined,
  }));
}

/** What a row currently reads as, with the customer's unsaved choice ahead of everything. */
export function choiceFor(row: MappingRow, draft: Record<string, string>): string {
  if (row.header in draft) return draft[row.header];
  if (row.stated === null) return IGNORE;
  if (row.stated) return row.stated;
  return row.detected ?? DETECT;
}

/**
 * Two of the customer's columns read as the same column of the form, or null.
 *
 * Worth catching here rather than only at the API: one of the two would silently win, and
 * which one is not something anybody should have to work out from the box totals.
 */
export function duplicateChoice(rows: MappingRow[], draft: Record<string, string>): string | null {
  const taken = new Map<string, string>();
  for (const row of rows) {
    const value = choiceFor(row, draft);
    if (value === IGNORE || value === DETECT) continue;
    const already = taken.get(value);
    if (already) return `"${already}" and "${row.header}" are both read as ${value}.`;
    taken.set(value, row.header);
  }
  return null;
}

/** What to send: the saved answers, with this session's changes folded in. */
export function mappingToSave(
  overrides: Record<string, string | null> | undefined,
  draft: Record<string, string>,
): Record<string, string | null> {
  const body: Record<string, string | null> = { ...(overrides ?? {}) };
  for (const [header, choice] of Object.entries(draft)) {
    // Back to detection: the customer's answer is removed rather than set to something.
    if (choice === DETECT) delete body[header];
    else body[header] = choice === IGNORE ? null : choice;
  }
  return body;
}

/**
 * How each of the customer's columns was read, and the means to disagree with it.
 *
 * Detection is three guesses at somebody else's spreadsheet and it is right most of the
 * time. When it was wrong there was nothing to be done from this screen: the mapping was
 * shown read-only beside a list of columns we could not place, and the only way forward was
 * to rename the headers in the file and upload it again. A column quietly matched to the
 * wrong box was worse, because the file went on looking fine.
 *
 * Saving sends the answer and the file is read again with it, so the box totals and the
 * checks below update to match. The answer is kept on the upload, so a later re-check does
 * not quietly undo it.
 */
export function ColumnMappingEditor({
  uploadId,
  formType,
  mapping,
  unmapped,
  overrides,
  editable,
}: {
  uploadId: string;
  formType: string | undefined;
  mapping: Record<string, string>;
  unmapped: string[];
  overrides: Record<string, string | null> | undefined;
  /** False once the file has been filed: the mapping then describes what was sent. */
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const schema = useQuery(formSchemaQuery(formType));
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);

  const columns = useMemo(() => {
    const properties = (schema.data as { properties?: Record<string, unknown> } | undefined)?.properties;
    return properties ? Object.keys(properties).sort((a, b) => a.localeCompare(b)) : [];
  }, [schema.data]);

  const rows = useMemo(
    () => mappingRows(mapping, unmapped, overrides),
    [mapping, unmapped, overrides],
  );

  const save = useMutation({
    mutationFn: () =>
      tabfile.PATCH('/v1/uploads/{uploadId}/column-mapping', {
        params: { path: { uploadId } },
        body: { mapping: mappingToSave(overrides, draft) },
      }),
    onSuccess: async () => {
      setDraft({});
      setFailure(null);
      await queryClient.invalidateQueries({ queryKey: ['upload', uploadId] });
    },
    onError: (error: unknown) => {
      const detail = (error as { detail?: string })?.detail;
      setFailure(detail ?? 'That mapping could not be saved. Check the columns and try again.');
    },
  });

  const changed = Object.keys(draft).length > 0;
  const duplicate = useMemo(() => duplicateChoice(rows, draft), [rows, draft]);

  if (rows.length === 0) return null;

  return (
    <Card>
      <SectionHeader
        title="Column mapping"
        description={
          editable
            ? 'How each column in your file was read. Change any that are wrong and save to read the file again.'
            : 'How each column in your file was read.'
        }
        actions={
          editable && changed ? (
            <>
              <Button variant="outline" disabled={save.isPending} onClick={() => setDraft({})}>
                Discard
              </Button>
              <Button
                variant="primary"
                disabled={save.isPending || duplicate !== null}
                onClick={() => save.mutate()}
              >
                {save.isPending ? <Spinner /> : null}
                Save and check again
              </Button>
            </>
          ) : null
        }
      />

      {duplicate ? (
        <p className="pb-4 text-base text-[color:var(--ts-danger-text)]">
          {duplicate} Each column of the form can only be read from one of yours.
        </p>
      ) : null}
      {failure ? <p className="pb-4 text-base text-[color:var(--ts-danger-text)]">{failure}</p> : null}

      <TableWrap>
        <Table>
          <THead>
            <tr>
              <TH>Your column</TH>
              <TH>Read as</TH>
            </tr>
          </THead>
          <TBody>
            {rows.map((row) => {
              const value = choiceFor(row, draft);
              return (
                <TR key={row.header}>
                  <TD>{row.header}</TD>
                  <TD>
                    {editable && columns.length > 0 ? (
                      <div className="w-[320px]">
                        <Combobox
                          ariaLabel={`What ${row.header} is read as`}
                          value={value}
                          onValueChange={(next) =>
                            setDraft((current) => ({ ...current, [row.header]: next }))
                          }
                          options={[
                            {
                              value: DETECT,
                              label: row.detected ? `${row.detected} (matched)` : 'Not used',
                            },
                            { value: IGNORE, label: 'Do not read this column' },
                            ...columns.map((column) => ({ value: column, label: column })),
                          ]}
                        />
                      </div>
                    ) : (
                      <span className="text-muted-foreground">
                        {value === IGNORE || value === DETECT ? 'Not used' : value}
                      </span>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </TableWrap>
    </Card>
  );
}
